import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { AssetsStore, Recommendation, RecommendationIntent, ViewStore } from '@coach-bot/data-access/shared';
import { convertPointsToMoneyString } from '@coach-bot/data-access/student';
import { AnalyticsEvent, getRecommendationResourceIds } from '@coach-bot/features/analytics';
import { faAngleRight, faSackDollar } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { ExtensionCommunicationService } from '@shared/extension';
import { AbstractComponent } from '@shared/ui';
import { BehaviorSubject, Observable, of, takeUntil } from 'rxjs';

const REWARD_POINTS = 10;

@Component({
  selector: 'st-recommendation',
  templateUrl: './recommendation.component.html',
  styleUrls: ['./recommendation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecommendationComponent extends AbstractComponent implements OnChanges {
  @Input() learning: boolean = false;
  @Input() recommendation: Recommendation;
  @Input() waitingForReward: boolean = false;

  readonly assetsPath$: BehaviorSubject<string> = this.assetsStore.assetsPath$;
  readonly currencySign$: BehaviorSubject<string> = this.viewStore.currencySign$;

  name$: Observable<string> = of('');
  subtitle$: Observable<string> = of('');
  title$: Observable<string> = of('');

  icons = {
    faAngleRight,
    faSackDollar,
  };
  inProgress: boolean = false;
  reward: string = convertPointsToMoneyString(REWARD_POINTS);

  constructor(
    private assetsStore: AssetsStore,
    private changeDetectorRef: ChangeDetectorRef,
    private extensionCommunicationService: ExtensionCommunicationService,
    private translateService: TranslateService,
    private viewStore: ViewStore,
  ) {
    super();
  }

  ngOnChanges(): void {
    this.inProgress = this.learning || this.waitingForReward;
    if (this.recommendation) {
      const isTest: boolean = this.recommendation.intent === RecommendationIntent.Test;
      this.title$ = this.translateService.stream(
        `student.recommendation.labels.${isTest ? 'assess' : 'learn'}`
      ).pipe(takeUntil(this.destroyed$));
      this.subtitle$ = this.translateService.stream(
        `student.recommendation.labels.${
          isTest ?
            this.recommendation.isPlacementTest ? 'take_placement_test' : 'take_test' :
            'master_skill'
        }`
      ).pipe(takeUntil(this.destroyed$));
      this.name$ = this.recommendation.isPlacementTest ?
        this.translateService.stream('student.recommendation.labels.placement_test_name')
          .pipe(takeUntil(this.destroyed$)) :
        of(`«${this.recommendation.name}»`);
      this.changeDetectorRef.detectChanges();
    }
  }

  async openRecommendedResource(): Promise<void> {
    if (this.inProgress) {
      return;
    }
    const [learningResourceId, placementQuizId] = getRecommendationResourceIds(this.recommendation);
    await this.extensionCommunicationService.trackAnalyticsEvent(AnalyticsEvent.LinkClick, {
      url: this.recommendation.url,
      learningResourceId,
      placementQuizId,
    });
    await this.extensionCommunicationService.navigateToUrl(this.recommendation.url);
  }
}
